import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQueryWithReauth } from 'utils/baseQueryWithReauth';
import { BaseMasterDataItem } from './masterDataTypes';
import { PRODUCT_LOV_TYPES, ALL_MODULE_TYPES } from 'types/LOVTypes';
import { ProcessedLOVData } from 'types/lovDataTypes';

const baseLovQuery = createBaseQueryWithReauth({
  baseUrl: process.env.REACT_APP_UAT_MASTER_DATA_API_URL || '',
});

export interface ListOfValuesResponse {
  status: number;
  message: string;
  data: BaseMasterDataItem[];
  fetchedAt?: string;
  totalItems?: number;
}

export interface ProcessedLOVResponse {
  status: number;
  message: string;
  rawData: BaseMasterDataItem[];
  processedData: ProcessedLOVData;
  fetchedAt: string;
  totalItems: number;
}

// Processing function moved from utils (synchronous version)
const processLovDataSync = (
  data: BaseMasterDataItem[],
  types: { [key: string]: string },
  moduleTypes?: { [key: string]: { [key: string]: string } }
): ProcessedLOVData => {
  const categorized: { [key: string]: BaseMasterDataItem[] } = {};
  const moduleData: { [key: string]: { [key: string]: BaseMasterDataItem[] } } = {};
  const typeIndex = new Map<string, BaseMasterDataItem[]>();
  const nameIndex = new Map<string, BaseMasterDataItem>();
  
  // Initialize categories
  if (types) {
    Object.values(types).forEach((type: string) => {
      categorized[type] = [];
    });
  }
  
  // Initialize module-specific categories
  if (moduleTypes) {
    Object.entries(moduleTypes).forEach(([module, moduleTypesObj]: [string, { [key: string]: string }]) => {
      moduleData[module] = {};
      Object.values(moduleTypesObj).forEach((type: string) => {
        moduleData[module][type] = [];
      });
    });
  }
  
  // Process all items (synchronous for RTK Query cache)
  data.forEach(item => {
    // Add to general categorized data
    if (categorized[item.type]) {
      categorized[item.type].push(item);
    }
    
    // Add to module-specific categories
    if (moduleTypes) {
      Object.entries(moduleTypes).forEach(([module, moduleTypesObj]: [string, { [key: string]: string }]) => {
        if (Object.values(moduleTypesObj).includes(item.type)) {
          if (moduleData[module] && moduleData[module][item.type]) {
            moduleData[module][item.type].push(item);
          }
        }
      });
    }
    
    // Create indexes
    if (!typeIndex.has(item.type)) {
      typeIndex.set(item.type, []);
    }
    typeIndex.get(item.type)!.push(item);
    nameIndex.set(item.name, item);
  });
  
  return {
    categorized,
    moduleData,
    indexes: {
      typeIndex: Array.from(typeIndex.entries()),
      nameIndex: Array.from(nameIndex.entries()),
    },
    metadata: {
      totalProcessed: data.length,
      processedAt: new Date().toISOString(),
      processingTime: 0, // Minimal processing time
    },
  };
};

export const lovApiSlice = createApi({
  reducerPath: 'lovApi',
  baseQuery: baseLovQuery,
  tagTypes: ['LOV'],
  keepUnusedDataFor: 0, // Don't keep data after logout
  endpoints: (builder) => ({
    getListOfValues: builder.query<ProcessedLOVResponse, { userId?: string }>({
      query: ({ userId }) => ({
        url: '/',
        method: 'GET',
        // Optional: include user context in request if needed
        headers: userId ? { 'User-Context': userId } : {},
      }),
      providesTags: (result, error, { userId }) => [
        { type: 'LOV', id: userId || 'anonymous' }
      ],
      // Force refetch when user changes
      forceRefetch: ({ currentArg, previousArg }) => {
        return currentArg?.userId !== previousArg?.userId;
      },
      transformResponse: (response: ListOfValuesResponse): ProcessedLOVResponse => {
        const startTime = performance.now();
        console.log('🚀 Processing LOV data in RTK Query cache...', response.data?.length || 0, 'items');
        
        // Process data synchronously in the cache
        const processedData = processLovDataSync(
          response.data || [],
          PRODUCT_LOV_TYPES,
          ALL_MODULE_TYPES
        );
        
        const processingTime = performance.now() - startTime;
        console.log('✅ LOV processing complete in', processingTime.toFixed(2), 'ms');
        
        return {
          status: response.status,
          message: response.message,
          rawData: response.data || [],
          processedData: {
            ...processedData,
            metadata: {
              ...processedData.metadata,
              processingTime,
            },
          },
          fetchedAt: new Date().toISOString(),
          totalItems: response.data?.length || 0,
        };
      },
    }),
  }),
});

export const { 
  useGetListOfValuesQuery,
  useLazyGetListOfValuesQuery,
  util: { invalidateTags, resetApiState }
} = lovApiSlice;

// Utility functions for auth integration
export const lovCacheUtils = {
  // Invalidate LOV cache for a specific user (triggers refetch)
  invalidateLovCache: (dispatch: any, userId?: string) => {
    console.log('🔄 Invalidating LOV cache for user:', userId || 'anonymous');
    dispatch(lovApiSlice.util.invalidateTags([
      { type: 'LOV', id: userId || 'anonymous' }
    ]));
  },

  // Clear all LOV cache data (on logout)
  clearLovCache: (dispatch: any) => {
    console.log('🗑️ Clearing all LOV cache data');
    dispatch(lovApiSlice.util.resetApiState());
  },

  // Prefetch LOV data for a user (on login)
  prefetchLovData: (dispatch: any, userId?: string) => {
    console.log('⚡ Prefetching LOV data for user:', userId || 'anonymous');
    dispatch(lovApiSlice.util.prefetch('getListOfValues', { userId }, { force: true }));
  }
}; 